import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AppScrollArea from '../../../../components/AppScrollArea'
import api from '../../../../api/client'

function toneStyles(tone) {
  if (tone === 'danger') return { bar: 'bg-error', tag: 'text-error' }
  if (tone === 'neutral') return { bar: 'bg-on-surface-variant/40', tag: 'text-on-surface-variant' }
  if (tone === 'secondary') return { bar: 'bg-secondary', tag: 'text-secondary' }
  return { bar: 'bg-primary', tag: 'text-primary' }
}

function WorkstreamItem({ item }) {
  const navigate = useNavigate()
  const tone = toneStyles(item.tone)

  function handleClick() {
    if (item.entityType === 'TASK') {
      navigate(`/nerve/task/entry?id=${item.entityId}`)
    } else {
      navigate(`/nerve/job/entry?id=${item.entityId}`)
    }
  }

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick() }}
      className="group flex cursor-pointer gap-4 rounded-lg bg-surface-container-lowest p-4 transition-all hover:ring-1 hover:ring-primary/20"
    >
      <span className={`h-auto w-1.5 rounded-full ${tone.bar}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-3">
          <span className={`text-[10px] font-bold uppercase tracking-widest ${tone.tag}`}>{item.tag}</span>
          <span className="max-w-[120px] truncate text-[10px] font-bold uppercase text-on-surface-variant">
            {item.userName || item.id}
          </span>
        </div>
        <h4 className="mt-1 font-bold text-on-surface">{item.title}</h4>
        <p className="mt-1 line-clamp-1 text-xs text-on-surface-variant">{item.note}</p>
      </div>
    </article>
  )
}

/**
 * Fetch a page of workstream items from the paginated endpoint.
 */
async function fetchWorkstreamPage(bucket, page, pageSize, filters) {
  const params = {
    bucket,
    page,
    page_size: pageSize,
  }
  if (filters?.companyId) params.company_id = filters.companyId
  if (filters?.projectId) params.project_id = filters.projectId
  if (filters?.selectedDate) params.selected_date = filters.selectedDate
  const { data } = await api.get('/dashboard/personal/workstream', { params })
  return data
}

export default function CriticalWorkstreamCard({
  items: initialCriticalItems,
  queueItems: initialQueueItems,
  criticalTotal = 0,
  queueTotal = 0,
  filters = {},
}) {
  const [activeTab, setActiveTab] = useState('critical')
  const [phase, setPhase] = useState('entered')
  const timerRef = useRef(null)

  // ── Per-tab pagination state ────────────────────────────────────────────
  const [criticalItems, setCriticalItems] = useState(initialCriticalItems || [])
  const [criticalPage, setCriticalPage] = useState(1)
  const [criticalHasMore, setCriticalHasMore] = useState(
    (initialCriticalItems || []).length < criticalTotal,
  )

  const [queueItems, setQueueItems] = useState(initialQueueItems || [])
  const [queuePage, setQueuePage] = useState(1)
  const [queueHasMore, setQueueHasMore] = useState(
    (initialQueueItems || []).length < queueTotal,
  )

  const [isLoadingMore, setIsLoadingMore] = useState(false)

  // Sync with parent when initial data changes (e.g. filter change)
  useEffect(() => {
    setCriticalItems(initialCriticalItems || [])
    setCriticalPage(1)
    setCriticalHasMore((initialCriticalItems || []).length < criticalTotal)
  }, [initialCriticalItems, criticalTotal])

  useEffect(() => {
    setQueueItems(initialQueueItems || [])
    setQueuePage(1)
    setQueueHasMore((initialQueueItems || []).length < queueTotal)
  }, [initialQueueItems, queueTotal])

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const switchTab = (nextTab) => {
    if (nextTab === activeTab) return

    if (timerRef.current) clearTimeout(timerRef.current)
    setPhase('leaving')
    timerRef.current = setTimeout(() => {
      setActiveTab(nextTab)
      setPhase('entering')
      requestAnimationFrame(() => {
        setPhase('entered')
      })
    }, 140)
  }

  // ── Load more handler ───────────────────────────────────────────────────
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore) return

    const isCritical = activeTab === 'critical'
    const currentPage = isCritical ? criticalPage : queuePage
    const nextPage = currentPage + 1
    const bucket = isCritical ? 'critical' : 'queue'

    setIsLoadingMore(true)
    try {
      const result = await fetchWorkstreamPage(bucket, nextPage, 10, filters)
      const newItems = Array.isArray(result?.items) ? result.items : []

      if (isCritical) {
        setCriticalItems((prev) => [...prev, ...newItems])
        setCriticalPage(nextPage)
        setCriticalHasMore(Boolean(result?.hasMore))
      } else {
        setQueueItems((prev) => [...prev, ...newItems])
        setQueuePage(nextPage)
        setQueueHasMore(Boolean(result?.hasMore))
      }
    } catch {
      // Silently fail — items stay as-is
    } finally {
      setIsLoadingMore(false)
    }
  }, [activeTab, criticalPage, queuePage, isLoadingMore, filters])

  const currentItems = activeTab === 'critical' ? criticalItems : queueItems
  const currentHasMore = activeTab === 'critical' ? criticalHasMore : queueHasMore
  const currentTotal = activeTab === 'critical' ? criticalTotal : queueTotal
  const panelClass =
    phase === 'leaving'
      ? 'opacity-0 translate-y-2'
      : phase === 'entering'
        ? 'opacity-0 -translate-y-2'
        : 'opacity-100 translate-y-0'

  return (
    <article className="rounded-lg bg-surface-container-low p-1">
      <div className="flex">
        <button
          type="button"
          onClick={() => switchTab('critical')}
          className={`flex-1 rounded-md py-3 text-xs font-bold uppercase tracking-widest transition-all ${
            activeTab === 'critical'
              ? 'bg-surface-container-lowest text-primary shadow-sm'
              : 'text-on-surface-variant hover:text-on-surface'
          }`}
        >
          Critical ({criticalTotal || criticalItems.length})
        </button>
        <button
          type="button"
          onClick={() => switchTab('queue')}
          className={`flex-1 rounded-md py-3 text-xs font-bold uppercase tracking-widest transition-all ${
            activeTab === 'queue'
              ? 'bg-surface-container-lowest text-primary shadow-sm'
              : 'text-on-surface-variant hover:text-on-surface'
          }`}
        >
          Queue ({queueTotal || queueItems.length})
        </button>
      </div>

      <div className={`p-5 transition-all duration-150 ${panelClass}`}>
        <AppScrollArea
          maxHeight="430px"
          onLoadMore={currentHasMore ? handleLoadMore : undefined}
          isLoading={isLoadingMore}
          hasMore={currentHasMore}
        >
          <div className="space-y-4 pr-2">
            {currentItems.map((item) => (
              <WorkstreamItem key={`${activeTab}-${item.id}`} item={item} />
            ))}
          </div>
        </AppScrollArea>
        {currentItems.length > 5 && currentTotal > currentItems.length ? (
          <p className="mt-3 text-[11px] font-medium text-on-surface-variant">
            <span className="material-symbols-outlined mr-1 align-middle text-[11px]">expand_more</span>
            Showing {currentItems.length} of {currentTotal} items — scroll for more
          </p>
        ) : null}
      </div>
    </article>
  )
}
